<template>
  <account-layout>
    <el-form :model="loginForm" :rules="loginRules" ref="loginForm">
      <el-row :gutter="60">
        <el-col :span="24">
          <div>Email</div>
          <el-form-item prop="email">
            <el-input class="input-space" type="email" v-model="loginForm.email" placeholder="Type here..."  @keyup.enter.native="login" />
          </el-form-item>
        </el-col>
        <el-col v-if="!usingSSO || showPasswordField" :span="24">
          <div>Password</div>
          <el-form-item prop="password">
            <el-input class="input-space" type="password" v-model="loginForm.password" placeholder="Type here..."  @keyup.enter.native="login"/>
          </el-form-item>
        </el-col>
        <el-col :span="24" v-if="errorMessage">
          <el-alert show-icon type="error" :title="errorMessage" :closable="false" />
        </el-col>
      </el-row>
      <el-row class="row-space">
        <el-col :span="24">
          <el-button
              v-if="usingSSO"
              type="primary"
              class="ac-btn"
              @click="verified ? (!showPasswordField ? loginSSO() : login()) : verify()"
            >{{ verified ? (!showPasswordField ? 'Login with SSO' : 'Login') : 'Next' }}
          </el-button>
          <el-button
              v-else
              type="primary"
              class="ac-btn"
              @click="verified ? login() : verify()"
            >{{ verified ? 'Login' : 'Next' }}
          </el-button>
        </el-col>
      </el-row>
    </el-form>
    <el-row>
      <el-col :span="12">
        <el-button type="text" @click="reset">Forgot password?</el-button>
      </el-col>
      <el-col :span="12" class="align-right">
        <el-button type="text" @click="signUp">Sign Up</el-button>
      </el-col>
    </el-row>
  </account-layout>
</template>

<script>
import _ from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import AccountLayout from './AccountLayout.vue';

export default {
  name: 'AccountLogin',
  components: {
    AccountLayout
  },
  props: {
    platform: String
  },
  data() {
    const checkEmail = (rule, value, callback) => {
      if (!value) {
        this.showPasswordField = false;
        return callback();
      }
      const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
      if (re.test(String(value).toLowerCase())) {
        if (value.match('^.*@assemblesports.(io|com|xyz)$')) {
          this.showPasswordField = true;
        } else if (this.usingSSO) {
          this.showPasswordField = false;
        }
        return callback();
      }

      if (this.usingSSO) {
        this.showPasswordField = false;
      }

      return callback(new Error('Invalid email address'));
    };

    const usingSSO = process.env.VUE_APP_NATIONAL_ID === '20'; // Bond University

    const { token } = this.$route?.query || {};

    if (token) {
      try {
        const decodedToken = JSON.parse(Buffer.from(token, 'base64').toString('ascii'));

        this.$store.commit('loading', true);
        this.$store.dispatch('user/login', decodedToken)
          .then(() => {
            if (this.$store.getters['user/roles'].length) {
              Promise.all([
                this.$store.dispatch('user/fetchModule'),
                this.$store.dispatch('national/setup'),
                this.$store.dispatch('season/setup')
              ]).then(() => {
                this.$router.push('dashboard').catch((err) => { this.$router.go(0); });
              });
            } else {
              this.$router.push('pending');
            }
          });
      } catch (e) {
        datadogRum.addError(e, {
          message: 'Error decoding token',
          token
        });
      }
      this.$store.commit('loading', false);
    }

    return {
      errorMessage: '',
      loginForm: {
        email: '',
        password: ''
      },
      loginRules: {
        email: [
          { validator: checkEmail, trigger: 'blur' }
        ],
        password: [
          { required: true, message: 'Password is required', trigger: 'blur' }
        ]
      },
      verified: true,
      usingSSO,
      showPasswordField: !usingSSO
    };
  },
  methods: {
    loginSSO() {
      if (!this.usingSSO) return;
      this.$store.commit('loading', true);
      window.location.href = `${process.env.VUE_APP_API_URL}/assemble/api/v1/account/${this.platform}/sso`;
    },
    login() {
      this.errorMessage = '';
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          try {
            this.$store.commit('loading', true);
            // const activeRole = localStorage.getItem('activeRole');
            const res = await this.$http.post(`/assemble/api/v1/account/${this.platform}/login`, {
              email: this.loginForm.email,
              password: this.loginForm.password,
              // activeRole
            });

            if (_.get(res, 'data.data.resetHash')) {
              this.$router.push({
                name: 'reset.submit',
                params: {
                  token: res.data.data.resetHash
                }
              });
            } else {
              await this.$store.dispatch('user/login', res.data);
              if (this.$store.getters['user/roles'].length) {
                await this.$store.dispatch('user/fetchModule');
                await this.$store.dispatch('national/setup');
                await this.$store.dispatch('season/setup');
                this.$router.push('dashboard').catch((err) => { this.$router.go(0); });
              } else {
                this.$router.push('pending');
              }
            }
            this.$store.commit('loading', false);
          } catch (e) {
            if (e?.response?.status === 401 && e?.response?.data?.verified === false) {
              this.verified = false;
            }
            if (e.name === 'NavigationDuplicated') {
              this.$router.go(0);
            } else {
              this.errorMessage = e?.response?.data?.message || 'An error has occured';
            }
            datadogRum.addError(e, {
              message: 'Error logging in',
              email: this.loginForm.email
            });
            this.$store.commit('loading', false);
          }
        }
      });
    },
    verify() {
      this.$router.push('verify');
    },
    reset() {
      this.$router.push('reset');
    },
    signUp() {
      this.$router.push('sign-up');
    }
  }
};
</script>

<style scoped lang="scss">
.content-card {
  margin-left: 1em;
  margin-right: 1em;
  margin-top: 2em;
  width: 90%;
  max-width: 30em;
}

.align-right {
  text-align: right;
}

.row-space {
  margin-top: 1em;
}

.input-space {
  margin-top: 0.5em;
}

.ac-btn {
  width: 100%;
  background-color: $highlight !important;
  border: 0;
}
</style>
